import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Search, Plus, Loader2, X, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal for searching assets and adding one-or-many to a target user.
 * Used from UsersPage → "Activos asignados" → "Añadir activo".
 *
 * Props:
 *   open               (bool)   Whether the modal is shown.
 *   onClose            (fn)     Called when closing without confirming.
 *   targetUser         (obj)    The user to assign assets to ({id, name, email, role}).
 *   onAssigned         (fn)     Called with the API response after a successful POST.
 *   excludeAssetIds    (str[])  Asset ids already assigned (filtered out of search results).
 */
const AssetAssignmentModal = ({ open, onClose, targetUser, onAssigned, excludeAssetIds = [] }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      // Use generic asset search; backend will filter by superadmin scope.
      if (query.trim()) params.set('search', query.trim());
      const r = await axios.get(`${API}/assets?${params.toString()}`, { withCredentials: true });
      const items = r.data?.items || [];
      const excludeSet = new Set(excludeAssetIds);
      setResults(items.filter(a => !excludeSet.has(a.id)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar');
    } finally {
      setLoading(false);
    }
  }, [query, excludeAssetIds]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(new Set());
    setResults([]);
    // Initial load: most recent assets.
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced search on query change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => search(), 300);
    return () => clearTimeout(t);
  }, [query, open, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirm = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/users/${targetUser.id}/assign-assets`,
        { asset_ids: ids },
        { withCredentials: true },
      );
      const d = r.data || {};
      const msg = [];
      if (d.assigned) msg.push(`${d.assigned} asignado${d.assigned === 1 ? '' : 's'}`);
      if (d.already_assigned) msg.push(`${d.already_assigned} ya asignado${d.already_assigned === 1 ? '' : 's'}`);
      if (d.not_found) msg.push(`${d.not_found} no encontrado${d.not_found === 1 ? '' : 's'}`);
      toast.success(msg.join(' · ') || 'OK');
      onAssigned && onAssigned(d);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al asignar');
    } finally {
      setSubmitting(false);
    }
  };

  const targetLabel = targetUser ? `${targetUser.name || targetUser.email} · ${targetUser.role}` : '';

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="asset-assign-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Plus className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Añadir activos</h3>
              <p className="text-xs text-slate-500 mt-0.5">Asignar a <strong>{targetLabel}</strong></p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="assign-modal-close">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por NPL, dirección, municipio o provincia"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:border-blue-500"
              autoFocus
              data-testid="assign-search-input"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <p className="text-sm">Buscando…</p>
            </div>
          ) : results.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-400">Sin resultados</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="border-b border-slate-200">
                  <th className="w-10 p-2"></th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">NPL</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Dirección</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Municipio</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Estado</th>
                </tr>
              </thead>
              <tbody>
                {results.map(a => {
                  const checked = selected.has(a.id);
                  return (
                    <tr
                      key={a.id}
                      className={`border-b border-slate-50 cursor-pointer hover:bg-blue-50/40 ${checked ? 'bg-blue-50/70' : ''}`}
                      onClick={() => toggle(a.id)}
                      data-testid={`assign-row-${a.id}`}
                    >
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(a.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{a.codigo_npl || a.id.slice(0, 8) + '…'}</td>
                      <td className="px-3 py-2 truncate max-w-[260px] text-slate-700">{a.direccion || '-'}</td>
                      <td className="px-3 py-2 text-slate-600">{a.municipio || '-'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{a.estado || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">
            {selected.size > 0 ? `${selected.size} seleccionado${selected.size === 1 ? '' : 's'}` : 'Selecciona uno o más activos'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-sm">Cancelar</button>
            <button
              onClick={confirm}
              disabled={submitting || selected.size === 0}
              className="px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1"
              data-testid="assign-confirm-btn"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
              Asignar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetAssignmentModal;
